import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { LucideAngularModule } from 'lucide-angular';

import { DateUtils } from '@shared/date-utils';

export interface PeriodoSelectOption {
  id: number | string;
  descricao: string;
  dataInicial?: string;
  dataFinal?: string;
  supportingText?: string;
  encerrado?: boolean;
}

@Component({
  selector: 'app-periodo-select',
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  templateUrl: './periodo-select.component.html',
  styleUrls: ['./periodo-select.component.scss']
})
export class PeriodoSelectComponent {
  @Input() options: PeriodoSelectOption[] = [];
  @Input() selectedId: number | string | null = null;
  @Input() disabled = false;
  @Input() label = 'Período';
  @Input() emptyLabel = 'Período';
  @Input() noResultsLabel = 'Nenhum resultado encontrado';
  @Input() dropdownWidthClass = 'w-64';
  @Input() clearable = false;

  @Output() selectedIdChange = new EventEmitter<number | string | null>();
  @Output() optionSelected = new EventEmitter<PeriodoSelectOption>();

  dropdownAberto = false;

  constructor(private elementRef: ElementRef<HTMLElement>) {}

  get selectedOption(): PeriodoSelectOption | undefined {
    return this.options.find((item) => item.id === this.selectedId);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node | null;
    if (target && !this.elementRef.nativeElement.contains(target)) {
      this.dropdownAberto = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.dropdownAberto = false;
  }

  toggleDropdown(): void {
    if (this.disabled) {
      return;
    }

    this.dropdownAberto = !this.dropdownAberto;
  }

  clearSelection(event?: Event): void {
    event?.stopPropagation();

    if (this.disabled || !this.clearable || this.selectedId === null) {
      return;
    }

    this.onSelectionChange(null);
  }

  onSelectionChange(id: number | string | null): void {
    this.dropdownAberto = false;
    this.selectedIdChange.emit(id);

    if (id === null) {
      return;
    }

    const option = this.options.find((item) => item.id === id);
    if (option) {
      this.optionSelected.emit(option);
    }
  }

  getSupportingText(option: PeriodoSelectOption | null | undefined): string {
    if (!option) {
      return '';
    }

    if (option.supportingText) {
      return option.supportingText;
    }

    if (option.dataInicial && option.dataFinal) {
      return `${DateUtils.formatarDataBrasileira(option.dataInicial)} até ${DateUtils.formatarDataBrasileira(option.dataFinal)}`;
    }

    return '';
  }

  isPeriodoFechado(option: PeriodoSelectOption | null | undefined): boolean {
    return !!option?.encerrado;
  }
}